import React, { useCallback, useEffect, useState } from "react";
import { Modal } from "@/common/components/modal";
import { SuccessModal } from "@/pages/register/success-modal";
import {
  COMPANY_TYPES_URL,
  PRODUCT_TYPE_URL,
  REGISTER_USER_URL,
  ALL_CLUSTERS,
} from "@/common/constants";
import useQuery from "@/hooks/useQuery";
import { useNotification } from "@/common/components/Notifications/NotificationProvider";
import Form, { FormInput } from "@/common/components/form/Form";
import { User } from "@/common/types";
import { useApiCallHandling } from "@/hooks/useApiCallHandling";
import { getProductList } from "@/pages/complete-registration/subs/data";
import { useSearchParams } from "react-router-dom";
import { ICompanyType } from "@/types/user.interface";

export const formInputs: FormInput[] = [
  {
    inputType: "text",
    prompt: "Company Name",
    name: "company_name",
    placeholder: "Enter company name",
    fullWidth: false,
    rules: {
      required: {
        expectedValue: true,
        errorMessage: "Company name is required",
      },
    },
  },
  {
    inputType: "input",
    loadingState: true,
    prompt: "Company type type",
    name: "company_type",
    placeholder: "Loading company types",
    fullWidth: false,
    readOnly: true,
  },
  {
    inputType: "input",
    prompt: "Product type",
    name: "product_type_id",
    placeholder: "Select Company type to load product types",
    fullWidth: false,
    readOnly: true,
  },
  {
    inputType: "selectInput",
    loadingState: true,
    prompt: "Market Cluster",
    name: "cluster_id",
    placeholder: "Loading market clusters",
    fullWidth: false,
    rules: {
      required: {
        expectedValue: true,
        errorMessage: "Market cluster is required",
      },
    },
  },
  {
    inputType: "email",
    prompt: "Company email address",
    name: "email",
    placeholder: "Enter your company's email address",
    fullWidth: false,
    rules: {
      isEmail: {
        expectedValue: true,
        errorMessage: "Not a valid email address",
      },
    },
  },
  {
    inputType: "text",
    prompt: "Company Phone number",
    name: "phone",
    placeholder: "Enter the company's phone number",
    fullWidth: false,
    rules: {
      validNigerianPhoneNumber: {
        expectedValue: true,
        errorMessage: "Invalid Nigerian phone number",
      },
    },
  },
  {
    inputType: "password",
    prompt: "Password",
    name: "password",
    placeholder: "Enter password",
    fullWidth: false,
    rules: {
      containSpecialChar: {
        expectedValue: true,
        errorMessage: "Include a special character",
      },
      minLength: {
        expectedValue: 8,
        errorMessage: "Should be atleast 8 characters",
      },
      containUppercase: {
        expectedValue: true,
        errorMessage: "Include uppercase letter",
      },
      containLowercase: {
        expectedValue: true,
        errorMessage: "Include lowercase letter",
      },
    },
  },
  {
    inputType: "password",
    prompt: "Confirm",
    name: "repeatPassword",
    placeholder: "Confirm password",
    fullWidth: false,
    rules: {
      compareWithExisting: {
        expectedValue: "password",
        errorMessage: "Does not match the password",
      },
    },
  },
];

export interface IStep1Data {
  email: string;
  phone_number?: number | undefined;
  company_type_id: string;
  product_type_id: string;
  company_name: string;
  market_cluster: string;
}

interface IUserDetail {
  handleChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => any;
  formData: IStep1Data;
  selectedCluster?: ICluster | null;
  onChangeCluster?: () => void;
}

export interface ICluster {
  id: string;
  name: string;
}

function UserDetail({ selectedCluster, onChangeCluster }: IUserDetail) {
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [actualForm, setActualForm] = useState<any>([]);
  const [, setFetching] = useState(false);
  const [loading, setLoading] = useState(false);
  const [, setRoles] = useState<any>([]);
  const [, setLoadingRolesFailed] = useState(false);

  const [searchParams] = useSearchParams();

  const paramEmail = searchParams.get("email");
  const paramCompanyType = searchParams.get("company_type");

  const notify = useNotification();

  const [isModalOpen] = useState(true);

  if (!isModalOpen) return null;
  const makeRequest = useApiCallHandling();

  const {
    loading: companyTypesLoading,
    data: companyTypes,
    error: companyTypesError,
  } = useQuery<ICompanyType[]>(COMPANY_TYPES_URL);

  const {
    loading: clustersLoading,
    data: clustersData,
    error: clustersError,
  } = useQuery<any[]>(ALL_CLUSTERS);

  const handleCLuster = (clusters: ICluster[]) => {
    return clusters.map((cluster) => ({
      id: cluster.id,
      title: cluster.name,
    }));
  };

  const handleFetchProduct = useCallback(
    (url: string) => {
      setActualForm((prev: any) => {
        const copy = [...prev];
        copy[2] = {
          inputType: "input",
          prompt: "Product type",
          name: "product_type_id",
          placeholder: "Loading product types",
          fullWidth: false,
          readOnly: true,
          loadingState: true,
        };
        return copy;
      });

      makeRequest(
        PRODUCT_TYPE_URL + `?company_type=${url}`,
        "GET",
        {},
        (data) => {
          const productType = {
            inputType: "selectInput",
            loadingState: false,
            returnId: true,
            prompt: "Product type",
            name: "product_type_id",
            placeholder: "Select Product Type",
            fullWidth: false,
            options: getProductList(data?.categories),
          };

          setActualForm((prev: any) => {
            const copy = [...prev];
            copy[2] = productType;
            return copy;
          });
        },
        console.log,
        console.log,
        {
          dontNotifyOnSucess: true,
        },
      );
    },
    [makeRequest],
  );

  const findAndUpdateCompanyTypeOptions = (companyType: string) => {
    const result = companyTypes?.find(
      (type: any) => type.title === companyType,
    );

    if (result) {
      const companyTypeInput = {
        inputType: "selectInput",
        returnId: true,
        prompt: "Company type",
        name: "company_type",
        placeholder: result.title,
        fullWidth: false,
        options: [...companyTypes],
        readOnly: true,
        initialValue: result.id,
      };

      setActualForm((prev: any) => {
        const copy = [...prev];
        copy[1] = companyTypeInput;
        return copy;
      });

      handleFetchProduct(result.title.toLowerCase());
    }
  };

  useEffect(() => {
    if (paramEmail && paramCompanyType) {
      let baseFormInputs = [...formInputs];

      if (paramEmail) {
        baseFormInputs[4] = {
          inputType: "text",
          prompt: "Company email address",
          name: "email",
          fullWidth: false,
          readOnly: true,
          placeholder: paramEmail,
          loadingState: false,
          initialValue: paramEmail,
        };

        setActualForm(baseFormInputs);
      }

      if (paramCompanyType && companyTypes?.length > 0) {
        findAndUpdateCompanyTypeOptions(paramCompanyType);
      }

      return;
    }

    setFetching(true);
    setActualForm(formInputs);
    setRoles(companyTypes);

    if (!companyTypesLoading && companyTypes?.length > 0) {
      const companyType = {
        inputType: "selectInput",
        returnId: true,
        prompt: "Company type",
        name: "company_type",
        indexLevel: 4,
        placeholder: "Select Company Type",
        fullWidth: false,
        options: companyTypes,
      };

      setActualForm((prev: any) => {
        const copy = [...prev];
        copy[1] = companyType;
        return copy;
      });
    }

    if (!companyTypesLoading && companyTypesError) {
      notify({
        type: "ERROR",
        message: "Unable to load roles, refresh the page to try again.",
      });
      setLoadingRolesFailed(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyTypes, companyTypesError, companyTypesLoading, paramCompanyType]);

  // ── Cluster field effect ───────────────────────────────────────────────────
  // Two cases handled here:
  //   A) selectedCluster prop is set (came from the NewReg modal) →
  //      pre-populate the field with the chosen cluster and lock it (readOnly).
  //      An optional suffix label lets the user trigger onChangeCluster.
  //   B) No selectedCluster prop (standalone / direct navigation) →
  //      fall back to the original interactive dropdown, keeping the
  //      Kanawa redirect behaviour from CLUSTER_REDIRECTS.

  useEffect(() => {
    if (clustersLoading) return;

    if (!clustersData || clustersData.length === 0) {
      if (clustersError) {
        notify({
          type: "ERROR",
          message:
            "Unable to load market clusters, refresh the page to try again.",
        });
      }
      return;
    }

    if (selectedCluster) {
      // Case A — pre-populate from modal selection, lock the field
      const preselectedCluster = {
        inputType: "selectInput",
        returnId: true,
        prompt: "Market Cluster",
        name: "cluster_id",
        placeholder: selectedCluster.name,
        fullWidth: false,
        readOnly: true,
        // initialValue drives the Form component to treat this as already selected
        initialValue: selectedCluster.id,
        options: handleCLuster(clustersData),
      };

      setActualForm((prev: any) => {
        const copy = [...prev];
        copy[3] = preselectedCluster;
        return copy;
      });
    } else {
      // Case B — interactive dropdown with Kanawa redirect (original behaviour)
      const marketCluster = {
        inputType: "selectInput",
        returnId: true,
        prompt: "Market Cluster",
        name: "cluster_id",
        placeholder: "Select Market Cluster",
        fullWidth: false,
        options: handleCLuster(clustersData),
      };

      setActualForm((prev: any) => {
        const copy = [...prev];
        copy[3] = marketCluster;
        return copy;
      });
    }
    // Re-run whenever the cluster prop or the fetched data changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clustersData, clustersLoading, clustersError, selectedCluster]);

  const onClose = useCallback(() => {
    setShowSuccessModal(false);
    window.location.href = "/";
  }, []);

  async function processFormInputs(values: User) {
    let {
      email,
      company_type,
      product_type_id,
      phone,
      password,
      repeatPassword,
      company_name,
      cluster_id,
    } = values;
    let referer = "";

    setLoading(true);
    makeRequest(
      REGISTER_USER_URL,
      "POST",
      {
        email,
        password,
        password_confirmation: repeatPassword,
        phone_number: phone,
        company_type_id: company_type,
        product_type_id,
        company_name,
        // fall back to whatever the Form collected for the free-choice case.
        cluster_id: selectedCluster?.id || cluster_id,
        invited_by: referer,
      },
      () => setShowSuccessModal(true),
      console.log,
      () => setLoading(false),
    );
  }

  const handleFormUpdate = (name: string, value: any) => {
    if (name === "company_type") {
      const userSelection = companyTypes.find((type: any) => type.id === value);
      if (userSelection) {
        handleFetchProduct(userSelection.title.toLowerCase());
      }
    }
  };

  return (
    <div>
      <Modal isOpen={showSuccessModal}>
        <SuccessModal onProceedClick={onClose} />
      </Modal>

      <div className="input_container">
        <div className="heading">MEDEX Registration</div>
        <div className="sub-heading">
          <div className="stepcounter">Step 1 of 5</div>
          <div className="stepcountTitle">User Details</div>
        </div>
      </div>

      {/* ── Wrong cluster affordance ─────────────────────────────────────────
           Only visible when a cluster was pre-selected via the modal.
           Gives the user an obvious escape hatch without disrupting the form. */}
      {selectedCluster && onChangeCluster && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            background: "rgba(76,175,120,0.08)",
            border: "1px solid rgba(76,175,120,0.25)",
            borderRadius: "8px",
            padding: "0.6rem 1rem",
            marginBottom: "1rem",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <span
              style={{
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                background: "#4CAF78",
                display: "inline-block",
                flexShrink: 0,
              }}
            />
            <span
              style={{ fontSize: "0.8rem", color: "#1B4D2E", fontWeight: 600 }}
            >
              {selectedCluster.name}
            </span>
          </div>
          <button
            type="button"
            onClick={onChangeCluster}
            style={{
              background: "none",
              border: "1px solid rgb(2, 12, 37)",
              borderRadius: "6px",
              padding: "0.25rem 0.65rem",
              fontSize: "0.75rem",
              color: "#1B4D2E",
              cursor: "pointer",
              fontWeight: 500,
              transition: "background 0.15s ease",
            }}
            onMouseEnter={(e) =>
              ((e.target as HTMLButtonElement).style.background =
                "rgba(27,77,46,0.07)")
            }
            onMouseLeave={(e) =>
              ((e.target as HTMLButtonElement).style.background = "none")
            }
          >
            Wrong cluster? Change
          </button>
        </div>
      )}

      <Form
        formInputs={actualForm}
        loadingState={loading}
        submitLabel="Register"
        processInputs={processFormInputs}
        login={false}
        biControl={false}
        handleBack={undefined}
        removePadding
        externalUpdateData={handleFormUpdate}
      />
    </div>
  );
}

export default UserDetail;
